import React from 'react';
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import Skeleton from 'react-loading-skeleton';

const PURPLE_BASE = '#5a1a9e';
const PURPLE_HIGHLIGHT = '#7b3bbf';

const PurpleTipSkeleton = () => (
  <div className="background-dark-purple">
    <Container className="week-tip">
      <Row className="py-5">
        <Col>
          <Skeleton
            width="82%"
            height={18}
            baseColor={PURPLE_BASE}
            highlightColor={PURPLE_HIGHLIGHT}
            style={{ marginBottom: '0.5rem' }}
          />
          <Skeleton width="65%" height={16} baseColor={PURPLE_BASE} highlightColor={PURPLE_HIGHLIGHT} />
        </Col>
      </Row>
    </Container>
  </div>
);

const RecipesSkeleton = () => (
  <Container>
    <Row className="align-items-end mb-3 mt-4">
      <Col xs="auto">
        <Skeleton width={150} height={22} />
      </Col>
      <Col xs="auto" style={{ marginBottom: '0.3rem' }}>
        <Skeleton width={110} height={16} />
      </Col>
    </Row>
    <Row className="pb-5">
      {[0, 1, 2].map(i => (
        <Col key={i} xs={12} sm={10} md={4} className="mb-4 mb-md-0">
          <Skeleton height={200} />
          <Skeleton width="80%" style={{ marginTop: '0.75rem' }} />
          <Skeleton width="60%" />
        </Col>
      ))}
    </Row>
  </Container>
);

const ChecklistCardSkeleton = ({ titleWidth = 220 }) => (
  <Card>
    <CardBody>
      <Skeleton width={titleWidth} height={24} style={{ marginBottom: '1rem' }} />
      {[0, 1, 2, 3, 4].map(i => (
        <div key={i} className="d-flex align-items-center py-3 border-bottom">
          <div className="px-3">
            <Skeleton circle width={24} height={24} />
          </div>
          <div style={{ flex: 1 }}>
            <Skeleton width="85%" />
          </div>
        </div>
      ))}
    </CardBody>
  </Card>
);

const ActionHourCardSkeleton = () => (
  <Card>
    <CardBody>
      <Skeleton width={180} height={24} style={{ marginBottom: '1rem' }} />
      <Skeleton height={200} />
      <Skeleton width="70%" style={{ marginTop: '1rem' }} />
    </CardBody>
  </Card>
);

const WeekInterstitialSkeleton = () => (
  <div className="background-dark-purple">
    <Container className="text-white py-5">
      <Row>
        <Col className="text-center">
          <Skeleton width={360} baseColor={PURPLE_BASE} highlightColor={PURPLE_HIGHLIGHT} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col className="text-center">
          <Skeleton width={260} height={42} baseColor={PURPLE_BASE} highlightColor={PURPLE_HIGHLIGHT} />
        </Col>
      </Row>
      <Row className="mt-3">
        <Col className="text-center">
          <Skeleton width={260} baseColor={PURPLE_BASE} highlightColor={PURPLE_HIGHLIGHT} />
        </Col>
      </Row>
    </Container>
  </div>
);

const ResourcesSkeleton = ({ titleWidth = 180 }) => (
  <Container className="pb-5">
    <Row className="mb-3">
      <Col>
        <Skeleton width={titleWidth} height={22} />
      </Col>
    </Row>
    <Row>
      {[0, 1, 2, 3].map(i => (
        <Col key={i} xs={6} md={3}>
          <Card>
            <Skeleton height={150} />
            <CardBody>
              <Skeleton width="90%" height={18} style={{ marginBottom: '0.5rem' }} />
              <Skeleton width="70%" height={14} />
            </CardBody>
          </Card>
        </Col>
      ))}
    </Row>
  </Container>
);

const WeekSkeleton = ({ isGettingStarted = false, isMobile = false, isTrackPreview = false }) => (
  <>
    <Container>
      <Row className="py-3">
        <Col>
          <Skeleton height={12} width="60%" />
        </Col>
      </Row>
      <Row>
        <Col className="text-center text-lg-left">
          <Skeleton width="70%" height={18} />
        </Col>
      </Row>
      <Row className="mt-2 mb-4">
        <Col className="text-center text-lg-left">
          <Skeleton width={320} height={36} />
        </Col>
      </Row>
    </Container>

    <div className="background-light-gray">
      <Container className="py-4">
        <Row>
          <Col xs={12}>
            <h4>
              <Skeleton width={250} />
            </h4>
          </Col>
          <Col xs={12} lg={7}>
            <div style={{ aspectRatio: '16/9', width: '100%' }}>
              <Skeleton height="100%" />
            </div>
          </Col>
          <Col xs={12} lg={5}>
            <Skeleton count={5} />
          </Col>
          <Col xs={12}>
            <Skeleton height={48} className="mb-5" />
          </Col>
        </Row>
      </Container>
    </div>

    {isGettingStarted ? <PurpleTipSkeleton /> : null}
    {!isGettingStarted ? <RecipesSkeleton /> : null}

    {(isMobile || isGettingStarted) && (
      <div className="background-light-gray">
        <Container>
          <Row className="py-5">
            <Col xs={12} lg={6} className="weekChecklist" style={{ marginTop: '3.6rem' }}>
              <ChecklistCardSkeleton titleWidth={260} />
            </Col>
            {isGettingStarted ? (
              <Col xs={12} lg={6} className="weekChecklist" style={{ marginTop: '3.6rem' }}>
                <ActionHourCardSkeleton />
              </Col>
            ) : null}
          </Row>
        </Container>
      </div>
    )}

    <PurpleTipSkeleton />
    {isGettingStarted && isMobile ? (
      <Container className="background-light-purple py-5">
        <Row>
          <Col xs={12}>
            <Skeleton width={320} height={30} style={{ marginBottom: '1rem' }} />
            <Skeleton width={360} style={{ marginBottom: '1rem' }} />
            {[0, 1, 2].map(i => (
              <Card key={i} className="mb-3">
                <CardBody>
                  <Skeleton width="55%" height={20} style={{ marginBottom: '0.75rem' }} />
                  <Skeleton count={3} />
                </CardBody>
              </Card>
            ))}
          </Col>
        </Row>
      </Container>
    ) : null}
    {isGettingStarted ? <RecipesSkeleton /> : null}
    <ResourcesSkeleton titleWidth={isGettingStarted ? 260 : 180} />

    {isGettingStarted ? <PurpleTipSkeleton /> : null}
    {!isGettingStarted && isMobile ? <WeekInterstitialSkeleton /> : null}

    {!isMobile ? (
      <div className="background-light-gray">
        <Container>
          <Row className="py-5">
            <Col xs={12} lg={6}>
              <ChecklistCardSkeleton />
            </Col>
            <Col xs={12} lg={6}>
              <ActionHourCardSkeleton />
            </Col>
          </Row>
        </Container>
      </div>
    ) : null}

    {!isGettingStarted ? (
      <>
        {!isTrackPreview && isMobile ? <ActionHourCardSkeleton /> : null}
        {!isMobile ? <WeekInterstitialSkeleton /> : null}
        {!isTrackPreview ? (
          <div className="py-5" style={{ backgroundColor: '#4A148C' }}>
            <Container>
              <Row className="justify-content-center mb-4">
                <Col xs={12} lg={8} className="text-center">
                  <Skeleton width={300} height={30} baseColor={PURPLE_BASE} highlightColor={PURPLE_HIGHLIGHT} />
                </Col>
              </Row>
              <Row className="justify-content-center">
                <Col xs={12} lg={8}>
                  <Card>
                    <Skeleton height={250} />
                    <CardBody>
                      <Skeleton width="70%" height={24} style={{ marginBottom: '1rem' }} />
                      <Skeleton width="50%" height={18} style={{ marginBottom: '1rem' }} />
                      <Skeleton count={3} style={{ marginBottom: '0.5rem' }} />
                      <Skeleton height={48} style={{ marginTop: '1rem' }} />
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Container>
          </div>
        ) : null}
      </>
    ) : (
      <Container className="py-5">
        <Row>
          <Col>
            <Skeleton width={260} height={26} style={{ marginBottom: '1rem' }} />
            {[0, 1, 2].map(i => (
              <Card key={i} className="mb-3">
                <CardBody>
                  <Skeleton width="55%" height={20} style={{ marginBottom: '0.75rem' }} />
                  <Skeleton count={2} />
                </CardBody>
              </Card>
            ))}
          </Col>
        </Row>
      </Container>
    )}
  </>
);

export default WeekSkeleton;
